/**
 * SyncJobProvider - app-wide context for background sync job state.
 *
 * One GET /sync-jobs/active on session start; polls only while a job is
 * running/pending (not every 60s when idle). Tab visibility triggers a
 * catch-up fetch when a job is active, or a one-shot rediscovery otherwise.
 */
import React from 'react';
import { buildApiUrl } from '../config';

/** Dispatched when active sync job goes away; useProductOnboarding listens (same string there). */
export const SYNC_JOB_FINISHED_EVENT = 'klaris:sync-job-finished';

// Re-use the shared type from DataSyncPage rather than duplicating it
export interface SyncStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  substeps?: SyncStep[];
}

export interface RunningSyncJob {
  job_id: string;
  tenant_id: string;
  connector_ids: string[];
  status: string;
  progress: { steps: SyncStep[] } | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  completed_at: string | null;
}

interface SyncJobContextValue {
  runningJob: RunningSyncJob | null;
  /**
   * Push a job update directly into the context - call with a running/pending job to
   * show the banner immediately (no wait for next poll), or with `null` to clear it
   * as soon as the job detail page observes completion.
   */
  notifyJobUpdate: (job: RunningSyncJob | null) => void;
  dismissJob: (jobId: string) => void;
  isDismissed: (jobId: string) => boolean;
}

export const SyncJobContext = React.createContext<SyncJobContextValue>({
  runningJob: null,
  notifyJobUpdate: () => {},
  dismissJob: () => {},
  isDismissed: () => false,
});

const POLL_INTERVAL = 12_000;
const DISMISSED_LS_KEY = 'klaris_dismissed_sync_jobs';

function loadDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_LS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(ids: Set<string>) {
  // Cap to last 50 so localStorage doesn't grow unboundedly
  localStorage.setItem(DISMISSED_LS_KEY, JSON.stringify(Array.from(ids).slice(-50)));
}

function isActiveJob(job: RunningSyncJob | null): job is RunningSyncJob {
  return !!job && (job.status === 'running' || job.status === 'pending');
}

function getAuthContext(): { tenantId: string; token: string } | null {
  try {
    const raw = localStorage.getItem('klaris_tenant');
    if (!raw) return null;
    const t = JSON.parse(raw);
    const tenantId: string = t?.tenant_id || t?.id || '';
    if (!tenantId) return null;
    const token = localStorage.getItem('klaris_jwt');
    if (!token) return null;
    return { tenantId, token };
  } catch {
    return null;
  }
}

export default function SyncJobProvider({ children }: { children: React.ReactNode }) {
  const [runningJob, setRunningJob] = React.useState<RunningSyncJob | null>(null);
  const [dismissed, setDismissed] = React.useState<Set<string>>(loadDismissed);
  const prevRunningJobIdRef = React.useRef<string | null>(null);
  const pollingRef = React.useRef(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const notifyJobUpdate = React.useCallback((job: RunningSyncJob | null) => {
    setRunningJob(job);
  }, []);

  // When a tracked job disappears from the active slot, onboarding may have advanced on the server
  // (e.g. data_model_built) even if the user left the job detail page - refetch checklist consumers.
  React.useEffect(() => {
    const id = runningJob?.job_id ?? null;
    const prev = prevRunningJobIdRef.current;
    if (prev !== null && id === null) {
      window.dispatchEvent(new CustomEvent(SYNC_JOB_FINISHED_EVENT));
    }
    prevRunningJobIdRef.current = id;
  }, [runningJob]);

  const dismissJob = React.useCallback((jobId: string) => {
    setDismissed(prev => {
      const next = new Set(prev);
      next.add(jobId);
      saveDismissed(next);
      return next;
    });
  }, []);

  const isDismissed = React.useCallback(
    (jobId: string) => dismissed.has(jobId),
    [dismissed],
  );

  const stopPolling = React.useCallback(() => {
    pollingRef.current = false;
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const fetchActiveJob = React.useCallback(async (signal?: AbortSignal): Promise<RunningSyncJob | null> => {
    const auth = getAuthContext();
    if (!auth) return null;
    try {
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${auth.tenantId}/sync-jobs/active`),
        { headers: { Authorization: `Bearer ${auth.token}` }, signal },
      );
      if (!resp.ok) return null;
      const data: RunningSyncJob | null = await resp.json();
      return data ?? null;
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return null;
      return null;
    }
  }, []);

  const pollOnce = React.useCallback(async (): Promise<RunningSyncJob | null> => {
    const active = await fetchActiveJob();
    setRunningJob(active);
    return active;
  }, [fetchActiveJob]);

  const startPollingLoop = React.useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;

    const tick = async () => {
      if (!pollingRef.current) return;
      if (document.hidden) {
        timeoutRef.current = setTimeout(tick, POLL_INTERVAL);
        return;
      }
      const active = await pollOnce();
      if (!pollingRef.current) return;
      if (isActiveJob(active)) {
        timeoutRef.current = setTimeout(tick, POLL_INTERVAL);
      } else {
        stopPolling();
      }
    };

    void tick();
  }, [pollOnce, stopPolling]);

  // Session start: one active check; poll only if a job is in flight.
  React.useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      const active = await fetchActiveJob(ctrl.signal);
      if (ctrl.signal.aborted) return;
      setRunningJob(active);
      if (isActiveJob(active)) startPollingLoop();
    })();
    return () => {
      ctrl.abort();
      stopPolling();
    };
  }, [fetchActiveJob, startPollingLoop, stopPolling]);

  // Job detail page / data-sync start pushes state immediately — begin polling.
  React.useEffect(() => {
    if (isActiveJob(runningJob)) {
      startPollingLoop();
    }
  }, [runningJob?.job_id, startPollingLoop]);

  // Tab visible again: refresh active job or rediscover (e.g. sync started in another tab).
  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void (async () => {
        const active = await pollOnce();
        if (isActiveJob(active)) startPollingLoop();
      })();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [pollOnce, startPollingLoop]);

  // Stable context object: only changes when one of the four values actually changes.
  const contextValue = React.useMemo(
    () => ({ runningJob, notifyJobUpdate, dismissJob, isDismissed }),
    [runningJob, notifyJobUpdate, dismissJob, isDismissed],
  );

  return (
    <SyncJobContext.Provider value={contextValue}>
      {children}
    </SyncJobContext.Provider>
  );
}
